import {
  type ElementType,
  type RefObject,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';

import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import { centerUnderPointer } from '@atlaskit/pragmatic-drag-and-drop/element/center-under-pointer';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import type { PlanningEntityType } from 'generated';
import { useRecoilState } from 'recoil';

import type { Event } from '../../types';
import type { EditAppointmentType } from 'RestAPIs/activities/types';

import {
  assignEmploymentEditAppointmentTypeAtom,
  selectedContextMenuOptionAtom,
  shouldShowPopoverDialogAtom
} from '../../atoms';

import { Box } from '@mui/material';
import PopoverWarningDialog from '../../components/PopoverWarningDialog/PopoverWarningDialog';
import { PopOverConfirmationContext } from '../../PopOverConfirmationContext';
import { ActivityCardPragmatic } from '../../CardsViewPragmatic/ActivityCardPragmatic/ActivityCardPragmatic';

import MultiDragPreviewPragmatic from '../MultiDragPreviewPragmatic';
import ChipDragPreviewPragmatic from '../ChipDragPreviewPragmatic/ChipDragPreviewPragmatic';
import { useSelector } from 'hooks';
import { ZoomLevel as DetailZoomLevel } from '../../components/Header/ZoomLevelMenu.enums';
import { Container } from './DraggableTimelineCardPragmatic.styles';
import { buildChipPreviewData } from './DraggableTimelineCardPragmatic.utils';

type CardProps = Omit<
  Event,
  | 'droppable'
  | 'category'
  | 'date'
  | 'end'
  | 'status'
  | 'isSubmittable'
  | 'isApprovable'
  | 'isDeletable'
> & {
  planningEntityId: string;
  planningEntityType: PlanningEntityType;
};

interface DraggableTimelineCardPragmaticProps extends CardProps {
  component?: ElementType<any>;
  selected?: boolean;
  readOnly?: boolean;
  /** Ref (not number) to avoid re-rendering across selection-count changes. */
  selectionCountRef?: RefObject<number>;
  /** Chip preview width (cursor follower); computed in the parent. */
  chipPreviewWidth: number;
  onHandleSelect?: (
    id: string,
    planningEntityType?: PlanningEntityType,
    employeeId?: string
  ) => void;
}

// Pragmatic to-plan card for the Timeline: same body as the CardsView card but
// registers a chip-shaped drag preview to match Timeline UX.
function DraggableTimelineCardPragmatic(
  props: DraggableTimelineCardPragmaticProps
) {
  'use memo';
  const {
    id,
    isRecurring,
    selected,
    planningEntityId,
    planningEntityType,
    readOnly,
    isAbsent,
    selectionCountRef: externalSelectionCountRef,
    chipPreviewWidth,
    onHandleSelect
  } = props;

  const [hovered, setHoveredState] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragDisabled, setDragDisabled] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const [
    {
      id: popoverOccurenceId,
      isUnassignment,
      isRouteUnassignment,
      isAssignEmployment,
      isDragAction
    },
    setShouldShowPopoverDialog
  ] = useRecoilState(shouldShowPopoverDialogAtom);

  const { isLoading, onClose, onHandleAppointmentConfirmation } = useContext(
    PopOverConfirmationContext
  );

  const [, setAssignEmploymentEditAppointmentType] = useRecoilState<
    EditAppointmentType | null | undefined
  >(assignEmploymentEditAppointmentTypeAtom);

  const [, setSelectedContextMenuOption] = useRecoilState(
    selectedContextMenuOptionAtom
  );

  const isPopOverId = popoverOccurenceId === id;
  const showPopoverDialog: boolean =
    !!isRecurring &&
    (!!isRouteUnassignment ||
      (isPopOverId && !isDragAction) ||
      popoverOccurenceId === 'IS_MULTIPLE');

  const allowDrag = !isAbsent && !readOnly && hovered && !dragDisabled;

  const canDragRef = useRef(false);

  // chipWidth into drag data so tooltip anchors at chip's top-left while cursor
  // sits at chip center. isToPlan: receivers branch on this for payload shape.
  const dragDataRef = useRef({
    id,
    planningEntityType,
    planningEntityId,
    isToPlan: true,
    chipWidth: chipPreviewWidth
  });

  const isSelectedRef = useRef(!!selected);

  // Read zoom here (in-tree); the detached preview can't use useSelector and
  // gets the value via a ref.
  const defaultDetailZoomLevel = useSelector(
    (state) => state.preferences.defaultZoomLevel
  );
  const detailZoomLevel = defaultDetailZoomLevel ?? DetailZoomLevel.STANDARD;

  // Pre-compute preview data here so render() in the detached tree is a pure
  // data → markup transform with no provider access.
  const chipPreviewDataRef = useRef(
    buildChipPreviewData(props, chipPreviewWidth, detailZoomLevel)
  );

  // Keep the refs above current for the mount-once draggable() callbacks.
  // Assigned in a layout effect (never during render) so the React Compiler can
  // optimize this component.
  useLayoutEffect(() => {
    canDragRef.current = !isAbsent && !readOnly && !dragDisabled;
    dragDataRef.current = {
      id,
      planningEntityType,
      planningEntityId,
      isToPlan: true,
      chipWidth: chipPreviewWidth
    };
    isSelectedRef.current = !!selected;
    chipPreviewDataRef.current = buildChipPreviewData(
      props,
      chipPreviewWidth,
      detailZoomLevel
    );
  });

  const setHovered = (value: boolean) => {
    setHoveredState(value);
  };

  // Register draggable() once. Draggable across the whole card: the native
  // dragstart targets the top-most card under the pointer, so no hover gate is
  // needed. Gating on hover left dead zones at the edges (hover not yet committed).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    return draggable({
      element: el,
      canDrag: () => canDragRef.current,
      getInitialData: () => ({ ...dragDataRef.current }),
      onGenerateDragPreview: ({ nativeSetDragImage }) => {
        const count = externalSelectionCountRef?.current ?? 0;
        const isMulti = isSelectedRef.current && count > 1;

        setCustomNativeDragPreview({
          nativeSetDragImage,
          getOffset: centerUnderPointer,
          render: ({ container }) => {
            const root = createRoot(container);
            // flushSync commits before Pragmatic's queueMicrotask snapshots.
            flushSync(() => {
              if (isMulti) {
                root.render(<MultiDragPreviewPragmatic count={count} />);
              } else {
                const data = chipPreviewDataRef.current;
                root.render(
                  <ChipDragPreviewPragmatic
                    width={data.width}
                    isClient={data.isClient}
                    isAbsent={data.isAbsent}
                    isRecurring={data.isRecurring}
                    selected={isSelectedRef.current}
                    hasWarnings={data.hasWarnings}
                    detailZoomLevel={data.detailZoomLevel}
                    label={data.label}
                  />
                );
              }
            });
            return () => root.unmount();
          }
        });
      },
      onDragStart: () => {
        setIsDragging(true);
      },
      onDrop: () => setIsDragging(false)
    });
  }, [externalSelectionCountRef]);

  function onSelectClick(e: React.MouseEvent) {
    if ((e.target as HTMLElement).classList.contains('MuiBackdrop-root')) {
      return;
    }
    onHandleSelect?.(id);
  }

  return (
    <Box
      component="li"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => {
        !selected && setHovered(false);
      }}
      sx={{
        '+ * .activity-card': {
          marginTop: '8px'
        },
        position: 'relative',
        zIndex: 2,
        '[data-drag-active] &': { pointerEvents: 'none' }
      }}
    >
      <PopoverWarningDialog
        open={showPopoverDialog && !isLoading}
        onClose={() => {
          if (isAssignEmployment) {
            setShouldShowPopoverDialog({ id: '' });
            setSelectedContextMenuOption(null);
            return setAssignEmploymentEditAppointmentType(null);
          }
          onClose?.();
          setShouldShowPopoverDialog({ id: '' });
        }}
        onClickAppointment={(editAppointmentType: EditAppointmentType) => {
          if (isAssignEmployment) {
            setShouldShowPopoverDialog({ id: '' });
            return setAssignEmploymentEditAppointmentType(editAppointmentType);
          }
          onHandleAppointmentConfirmation?.(
            editAppointmentType,
            isUnassignment
          );
          setShouldShowPopoverDialog({ id: '' });
        }}
      >
        <Container
          className="draggable-card"
          ref={containerRef}
          onClick={onSelectClick}
          sx={{
            cursor: allowDrag ? 'grab' : 'default',
            opacity: isDragging ? 0.5 : 1,
            userSelect: 'none'
          }}
        >
          <ActivityCardPragmatic
            {...props}
            selected={!!selected}
            setDragDisabled={setDragDisabled}
            hovered={hovered}
            setHovered={setHovered}
            isTaskList
          />
        </Container>
      </PopoverWarningDialog>
    </Box>
  );
}

export default DraggableTimelineCardPragmatic;
